/**
 * Player-display channel. The GM screen broadcasts "show this" messages; a separate
 * browser window/tab (the player display) subscribes and shows them fullscreen.
 * Uses BroadcastChannel with a localStorage fallback, and replays the last message.
 */

export type DisplayPayload = {
  src?: string;
  title?: string;
  caption?: string;
  fit?: 'cover' | 'contain';
};

export interface DisplayMessage {
  type:
    | 'image'
    | 'character'
    | 'npc'
    | 'item'
    | 'monster'
    | 'room'
    | 'clue'
    | 'text'
    | 'clear';
  payload: DisplayPayload;
  instanceId?: string;
  ts: number;
  nonce: string;
}

const CHANNEL = 'gmbinder-display';
const LAST_KEY = 'gmbinder:display:last';

function makeNonce(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function sendDisplay(
  type: DisplayMessage['type'],
  payload: DisplayPayload,
  instanceId?: string,
): void {
  const msg: DisplayMessage = { type, payload, instanceId, ts: Date.now(), nonce: makeNonce() };
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(msg);
    bc.close();
  } catch {
    /* BroadcastChannel unavailable */
  }
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify(msg));
  } catch {
    /* storage full / unavailable */
  }
}

export function clearDisplay(instanceId?: string): void {
  sendDisplay('clear', {}, instanceId);
}

export function showImage(src: string, opts: Partial<DisplayPayload> = {}, instanceId?: string) {
  sendDisplay('image', { src, fit: 'contain', ...opts }, instanceId);
}

export interface SubscribeOptions {
  replayLast?: boolean;
}

/** Subscribe to display messages. Returns an unsubscribe function. */
export function subscribeDisplay(
  handler: (msg: DisplayMessage) => void,
  opts: SubscribeOptions = {},
): () => void {
  let bc: BroadcastChannel | null = null;
  const onStorage = (e: StorageEvent) => {
    if (e.key === LAST_KEY && e.newValue) {
      try {
        handler(JSON.parse(e.newValue));
      } catch {
        /* ignore */
      }
    }
  };

  try {
    bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => handler(e.data as DisplayMessage);
  } catch {
    /* fall back to storage only */
  }
  window.addEventListener('storage', onStorage);

  if (opts.replayLast) {
    try {
      const raw = localStorage.getItem(LAST_KEY);
      if (raw) handler(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }

  return () => {
    bc?.close();
    window.removeEventListener('storage', onStorage);
  };
}
